/**
 * Planner: long-term cash + investment projection from a salary/spend/invest scenario.
 *
 * Income comes in *chapters* — each chapter has an `effective_from` date,
 * a starting `monthly_salary`, and a `salary_growth_pct` that compounds from
 * the chapter's start date. The simulator walks month-by-month and at each
 * month picks the chapter active for that month.
 *
 * Server is the source of truth: a `Plan` mirrors `TrajectoryPlanOut` from
 * the API. localStorage is no longer used.
 *
 * `simulateWithBands` adds a probabilistic view: the configured ROI is the
 * geometric mean (median) and S&P historical volatility (~15% annual) drives
 * a Monte-Carlo cone around it. Seeded PRNG so re-renders are stable.
 */

import type { components } from './api.types';

export type RecurringExpense = { id: string; name: string; price: number };

export type IncomeChapter = {
  id: string;
  effectiveFrom: string; // ISO date
  monthlySalary: number;
  salaryGrowthPct: number;
  note: string | null;
};

export type Plan = {
  id: string;
  name: string;
  isDefault: boolean;
  currentAge: number;
  years: number;
  startingCashOverride: number | null;
  startingHoldingsOverride: number | null;
  monthlyInvestment: number;
  investmentRoiPct: number;
  groceries: number;
  goingOut: number;
  randomMonthly: number;
  yearlyVacations: number;
  recurringExpenses: RecurringExpense[];
  incomes: IncomeChapter[];
};

export type StartingBalance = { cash: number; invested: number };

export type PlannerPoint = {
  year: number;
  cash: number;
  invested: number;
  total: number;
  monthlyNet: number;
  monthlyIncome: number;
};

export type PlannerBandPoint = PlannerPoint & {
  // 10th / 90th percentile of total wealth given S&P-like return volatility.
  // Volatility only applies to the invested portion; cash is treated as risk-free.
  investedLow: number;
  investedHigh: number;
  // Arithmetic mean (expected value) across MC paths. For log-normal returns
  // this sits *above* the median: skew makes the few good paths drag the
  // average up, so "expected" wealth > "typical" wealth.
  investedMean: number;
  totalLow: number;
  totalHigh: number;
  totalMean: number;
};

// Annual std-dev of S&P 500 nominal total returns. ~15-17% historically;
// 0.15 keeps the cone slightly tighter than the worst-case empirical figure.
export const S_AND_P_ANNUAL_VOLATILITY = 0.15;

// --- API mapping ----------------------------------------------------------

type ApiPlan = components['schemas']['TrajectoryPlanOut'];
type ApiIncome = components['schemas']['TrajectoryIncomeOut'];

/** Convert a server plan into the client-side numeric shape. */
export function planFromApi(p: ApiPlan): Plan {
  return {
    id: p.id,
    name: p.name,
    isDefault: p.is_default,
    currentAge: p.current_age,
    years: p.years,
    startingCashOverride:
      p.starting_cash_override == null ? null : Number(p.starting_cash_override),
    startingHoldingsOverride:
      p.starting_holdings_override == null ? null : Number(p.starting_holdings_override),
    monthlyInvestment: Number(p.monthly_investment),
    investmentRoiPct: p.investment_roi_pct,
    groceries: Number(p.groceries),
    goingOut: Number(p.going_out),
    randomMonthly: Number(p.random_monthly),
    yearlyVacations: Number(p.yearly_vacations),
    recurringExpenses: p.recurring_expenses.map((r) => ({
      id: r.id,
      name: r.name,
      price: Number(r.price),
    })),
    incomes: p.incomes.map(incomeFromApi),
  };
}

export function incomeFromApi(i: ApiIncome): IncomeChapter {
  return {
    id: i.id,
    effectiveFrom: i.effective_from,
    monthlySalary: Number(i.monthly_salary),
    salaryGrowthPct: i.salary_growth_pct,
    note: i.note ?? null,
  };
}

// --- Spending helpers -----------------------------------------------------

export function effectiveStartingBalance(
  p: Plan,
  liveAccountsEur: number,
  liveHoldingsEur: number,
): StartingBalance {
  return {
    cash: p.startingCashOverride ?? liveAccountsEur,
    invested: p.startingHoldingsOverride ?? liveHoldingsEur,
  };
}

export function monthlySpendingSubtotal(p: Plan): number {
  return p.groceries + p.goingOut + p.randomMonthly + p.yearlyVacations / 12;
}

export function recurringExpensesTotal(items: RecurringExpense[]): number {
  return items.reduce((s, x) => s + (Number.isFinite(x.price) ? x.price : 0), 0);
}

export function monthlyFixedExpenses(p: Plan): number {
  return (
    p.groceries +
    p.goingOut +
    p.randomMonthly +
    recurringExpensesTotal(p.recurringExpenses) +
    p.yearlyVacations / 12
  );
}

/**
 * Find the most-recent income chapter whose `effective_from` is on or
 * before the given date. Returns `null` only when the plan has zero
 * chapters (server enforces ≥1, but the client may briefly hold an
 * empty list during create-form editing).
 */
export function activeChapterAt(
  incomes: IncomeChapter[],
  iso: string,
): IncomeChapter | null {
  if (incomes.length === 0) return null;
  // Defensive sort; the server returns these ascending but be safe.
  const sorted = [...incomes].sort((a, b) =>
    a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : 0,
  );
  let active = sorted[0];
  for (const c of sorted) {
    if (c.effectiveFrom <= iso) active = c;
    else break;
  }
  return active;
}

/** Initial salary used by the planner UI's "today" summary cards. */
export function currentMonthlySalary(p: Plan): number {
  const today = isoToday();
  const c = activeChapterAt(p.incomes, today);
  return c?.monthlySalary ?? 0;
}

// --- Simulator ------------------------------------------------------------

type ChapterState = {
  startMonth: number; // first simulation-month index where this chapter is active
  monthlySalary: number;
  monthlyGrowth: number; // per-month multiplier - 1 (so salary *= 1+r each month)
};

/**
 * Pre-compute when each chapter "kicks in" relative to month 0 (today),
 * sorted by start month. Chapters with `effective_from` in the past clamp
 * to month 0 — we don't simulate history, only "from now forward".
 */
function buildChapterSchedule(p: Plan, totalMonths: number): ChapterState[] {
  if (p.incomes.length === 0) return [];
  const today = startOfMonth(new Date());

  const states: ChapterState[] = p.incomes.map((c) => {
    const eff = startOfMonth(parseIsoDate(c.effectiveFrom));
    const offset = monthsBetween(today, eff);
    return {
      startMonth: Math.max(0, offset),
      monthlySalary: c.monthlySalary,
      monthlyGrowth: (1 + c.salaryGrowthPct / 100) ** (1 / 12) - 1,
    };
  });
  states.sort((a, b) => a.startMonth - b.startMonth);

  // If multiple chapters all clamped to month 0 (e.g. the user has both
  // "started 2020" and "raise 2024" rows but today is 2026), keep only the
  // latest of those — the others are historical noise.
  const filtered: ChapterState[] = [];
  for (const s of states) {
    if (filtered.length > 0 && filtered[filtered.length - 1].startMonth === s.startMonth) {
      filtered[filtered.length - 1] = s;
    } else if (s.startMonth < totalMonths) {
      filtered.push(s);
    }
  }
  return filtered;
}

/**
 * Salary for simulation month `m` given the chapter schedule. Walks the
 * schedule once to find the active chapter, then compounds from the
 * chapter's start month.
 */
function salaryAtMonth(schedule: ChapterState[], m: number): number {
  if (schedule.length === 0) return 0;
  let active = schedule[0];
  for (const s of schedule) {
    if (s.startMonth <= m) active = s;
    else break;
  }
  if (m < active.startMonth) return 0;
  const elapsed = m - active.startMonth;
  return active.monthlySalary * (1 + active.monthlyGrowth) ** elapsed;
}

export function simulate(p: Plan, start: StartingBalance): PlannerPoint[] {
  const months = Math.max(0, Math.round(p.years * 12));
  const fixed = monthlyFixedExpenses(p);
  const investRateMonthly = p.investmentRoiPct / 100 / 12;
  const schedule = buildChapterSchedule(p, months + 1);

  let cash = start.cash;
  let invested = start.invested;

  const points: PlannerPoint[] = [
    {
      year: 0,
      cash,
      invested,
      total: cash + invested,
      monthlyNet: salaryAtMonth(schedule, 0) - fixed - p.monthlyInvestment,
      monthlyIncome: salaryAtMonth(schedule, 0),
    },
  ];

  for (let m = 1; m <= months; m++) {
    const salary = salaryAtMonth(schedule, m);
    invested = invested * (1 + investRateMonthly) + p.monthlyInvestment;
    cash += salary - fixed - p.monthlyInvestment;
    if (m % 12 === 0) {
      points.push({
        year: m / 12,
        cash,
        invested,
        total: cash + invested,
        monthlyNet: salary - fixed - p.monthlyInvestment,
        monthlyIncome: salary,
      });
    }
  }
  return points;
}

/**
 * Probabilistic simulation: treats `investmentRoiPct` as the *median* (geometric
 * mean) annual return and adds log-normal monthly noise calibrated to S&P
 * historical volatility. Returns each yearly point with low/high bands at the
 * 10th and 90th percentiles of the invested portion, plus the deterministic
 * median path in `cash`/`invested`/`total`.
 *
 * Cash is treated as risk-free, so the bands only widen the invested portion.
 *
 * Uses a seeded PRNG so repeated calls with the same inputs give identical
 * curves — important for stable rendering across React renders.
 */
export function simulateWithBands(
  p: Plan,
  start: StartingBalance,
  opts: { paths?: number; volatility?: number; seed?: number } = {},
): PlannerBandPoint[] {
  const PATHS = opts.paths ?? 400;
  const sigma = opts.volatility ?? S_AND_P_ANNUAL_VOLATILITY;
  const seed = opts.seed ?? 0xC0FFEE;

  const median = simulate(p, start);
  const months = Math.max(0, Math.round(p.years * 12));
  const yearsCount = median.length;
  if (months === 0 || sigma === 0 || (start.invested === 0 && p.monthlyInvestment === 0)) {
    return median.map((m) => ({
      ...m,
      investedLow: m.invested,
      investedHigh: m.invested,
      investedMean: m.invested,
      totalLow: m.total,
      totalHigh: m.total,
      totalMean: m.total,
    }));
  }

  const r = p.investmentRoiPct / 100;
  const monthlyLogMedian = Math.log(1 + r / 12);
  const monthlyLogStd = sigma / Math.sqrt(12);

  const rand = mulberry32(seed);
  const investedSamples: number[][] = Array.from({ length: yearsCount }, () => []);
  investedSamples[0] = Array.from({ length: PATHS }, () => start.invested);

  for (let path = 0; path < PATHS; path++) {
    let invested = start.invested;
    for (let m = 1; m <= months; m++) {
      const z = standardNormal(rand);
      const logRet = monthlyLogMedian + monthlyLogStd * z;
      invested = invested * Math.exp(logRet) + p.monthlyInvestment;
      if (m % 12 === 0) {
        investedSamples[m / 12].push(invested);
      }
    }
  }

  return median.map((point, i) => {
    const samples = investedSamples[i];
    const investedMean =
      samples.length === 0 ? point.invested : samples.reduce((s, x) => s + x, 0) / samples.length;
    samples.sort((a, b) => a - b);
    const low = quantile(samples, 0.1);
    const high = quantile(samples, 0.9);
    return {
      ...point,
      investedLow: low,
      investedHigh: high,
      investedMean,
      totalLow: point.cash + low,
      totalHigh: point.cash + high,
      totalMean: point.cash + investedMean,
    };
  });
}

// --- Helpers --------------------------------------------------------------

function quantile(sortedAsc: number[], q: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const pos = q * (sortedAsc.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (pos - lo) * (sortedAsc[hi] - sortedAsc[lo]);
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function standardNormal(rand: () => number): number {
  const u = Math.max(rand(), 1e-12);
  const v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

export function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
